import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import useFormSecurity from '../../../hooks/useFormSecurity';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { 
  FiUser, FiMail, FiArrowRight, FiArrowLeft, FiX, FiStar, FiShield,
  FiPhone, FiBriefcase, FiGlobe, FiLinkedin, FiMapPin, FiAward, FiClock, FiFileText,
  FiBookOpen, FiCheckCircle
} = FiIcons;

const GEOGRAPHY_OPTIONS = [
  'Hele Norge', 'Oslo', 'Østlandet', 'Vestlandet', 'Sørlandet', 
  'Midt-Norge', 'Nord-Norge', 'Digitalt / hele landet'
];

const COMPETENCY_OPTIONS = [
  'Arbeidsmiljø', 'Varslingssaker', 'Arbeidsrett', 'Granskning', 
  'Mobbing og trakassering', 'Diskriminering', 'Seksuell trakassering', 
  'Ledelse og organisasjon', 'Økonomiske misligheter', 'Korrupsjon', 
  'Compliance', 'Personvern', 'HMS', 'Sikkerhet', 'Offentlig sektor', 
  'HR', 'Konflikthåndtering', 'Revisjon', 'Juridiske undersøkelser', 'Annet'
];

const BACKGROUND_OPTIONS = [
  'Advokat', 'Jurist', 'Revisor', 'HR', 'HMS', 'Gransker', 
  'Compliance', 'Psykolog', 'Organisasjonsrådgiver', 'Annet'
];

const UtrederRegister = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addUtreder } = useData();
  const { validateSubmission, HoneypotField } = useFormSecurity('utreder_register');

  const preSelectedPlan = location.state?.plan || 'basic';

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
    title_role: '',
    company_name: '',
    org_number: '',
    website: '',
    linkedin: '',
    geography: [],
    competence_tags: [],
    years_experience: '',
    education: '',
    experience_summary: '',
    certifications: '',
    professional_background: '',
    bio: '',
    max_capacity: '3',
    availability: 'now',
    subscription_plan: preSelectedPlan,
    qualification_course_interest: false,
    terms_acceptance: false,
    confirm_correct: false,
    confirm_confidentiality: false,
    confirm_conflict: false
  });

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleSelection = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      const updated = current.includes(value) 
        ? current.filter(item => item !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateSubmission()) return;
    
    if (!formData.terms_acceptance) {
      setError('Du må godta taushets- og profesjonsvilkårene.');
      return;
    }

    if (!formData.confirm_correct || !formData.confirm_confidentiality || !formData.confirm_conflict) {
      setError('Du må bekrefte alle punktene nederst i skjemaet.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // VIKTIG: Bruk await for addUtreder
      const result = await addUtreder({
        ...formData,
        roles: ['utreder'],
        status: 'pending_review',
        active: false,
        createdAt: new Date().toISOString()
      });

      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error || 'Kunne ikke registrere. Prøv igjen.');
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError('En uventet feil oppstod. Prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiShield} className="text-purple-600 text-3xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Søknad registrert!</h2>
          <p className="text-gray-600 mb-6">
            Din profil er nå til godkjenning hos vårt kvalitetsteam. 
            Du vil motta en e-post når din profil er verifisert og synlig i registeret.
          </p>
          <button 
            onClick={() => navigate('/')}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Tilbake til forsiden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 relative">
      <button 
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 p-2 bg-white rounded-full shadow-md text-gray-600 hover:text-indigo-600 transition-colors flex items-center gap-1 text-sm font-medium z-10"
      >
        <SafeIcon icon={FiArrowLeft} />
        Tilbake
      </button>

      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-indigo-900 p-8 text-white text-center">
          <SafeIcon icon={FiUser} className="text-4xl mx-auto mb-4 text-indigo-200" />
          <h1 className="text-3xl font-bold">Registrering Utreder</h1>
          <p className="text-indigo-200 mt-2">Bli en del av vårt nasjonale nettverk av fagfolk</p>
        </div>

        <div className="p-8">
          <HoneypotField />
          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 text-sm flex items-center gap-2">
              <SafeIcon icon={FiX} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">1. Velg Synlighetsnivå</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div 
                  onClick={() => setFormData({...formData, subscription_plan: 'basic'})}
                  className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${formData.subscription_plan === 'basic' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-200'}`}
                >
                  <div className="font-bold text-gray-900">Basic</div>
                  <div className="text-xl font-bold text-indigo-600 my-1">990,-<span className="text-xs text-gray-500 font-normal">/mnd</span></div>
                  <p className="text-xs text-gray-500">Standard oppføring i registeret.</p>
                </div>

                <div 
                  onClick={() => setFormData({...formData, subscription_plan: 'pro'})}
                  className={`border-2 rounded-xl p-4 cursor-pointer transition-all relative ${formData.subscription_plan === 'pro' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-200'}`}
                >
                  <div className="absolute top-0 right-0 bg-indigo-600 text-white text-[10px] px-2 py-0.5 rounded-bl-lg font-bold">ANBEFALT</div>
                  <div className="font-bold text-gray-900 flex items-center gap-1">Pro <SafeIcon icon={FiStar} className="text-yellow-500 text-xs" /></div>
                  <div className="text-xl font-bold text-indigo-600 my-1">2 990,-<span className="text-xs text-gray-500 font-normal">/mnd</span></div>
                  <p className="text-xs text-gray-500">Microsite, prioritert matching og branding.</p>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">2. Personlig Informasjon</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fullt Navn *</label>
                  <input 
                    type="text" 
                    required
                    value={formData.full_name}
                    onChange={e => setFormData({...formData, full_name: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">E-post (Login) *</label>
                  <input 
                    type="email" 
                    required
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefon *</label>
                  <input 
                    type="tel" 
                    required
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stilling / profesjonell tittel *</label>
                  <input 
                    type="text" 
                    required
                    value={formData.title_role}
                    onChange={e => setFormData({...formData, title_role: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" 
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sett passord *</label>
                  <input 
                    type="password" 
                    required
                    value={formData.password}
                    onChange={e => setFormData({...formData, password: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" 
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">3. Virksomhet</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Firma / arbeidsgiver *</label>
                  <input 
                    type="text" 
                    required
                    value={formData.company_name}
                    onChange={e => setFormData({...formData, company_name: e.target.value})}
                    placeholder="Selvstendig / Firma AS"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Organisasjonsnummer</label>
                  <input 
                    type="text" 
                    value={formData.org_number}
                    onChange={e => setFormData({...formData, org_number: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nettside</label>
                  <input 
                    type="url" 
                    value={formData.website}
                    onChange={e => setFormData({...formData, website: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn-profil</label>
                  <input 
                    type="url" 
                    value={formData.linkedin}
                    onChange={e => setFormData({...formData, linkedin: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" 
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">4. Geografi (Arbeidsområde) *</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {GEOGRAPHY_OPTIONS.map(opt => (
                  <button
                    key={opt} type="button"
                    onClick={() => toggleSelection('geography', opt)}
                    className={`p-2 rounded-lg border text-xs font-bold transition-all ${formData.geography.includes(opt) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">5. Kompetanseområder *</h3>
              <div className="flex flex-wrap gap-2">
                {COMPETENCY_OPTIONS.map(opt => (
                  <button
                    key={opt} type="button"
                    onClick={() => toggleSelection('competence_tags', opt)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${formData.competence_tags.includes(opt) ? 'bg-indigo-100 text-indigo-700 border-indigo-300' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-200'}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">6. Erfaring og Kvalifikasjoner</h3>
              <div className="grid gap-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Profesjon / fagbakgrunn *</label>
                    <select 
                      required
                      value={formData.professional_background}
                      onChange={e => setFormData({...formData, professional_background: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Velg...</option>
                      {BACKGROUND_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Antall års relevant erfaring *</label>
                    <input 
                      type="number" required min="0"
                      value={formData.years_experience}
                      onChange={e => setFormData({...formData, years_experience: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" 
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Utdanning *</label>
                  <input 
                    type="text" required
                    value={formData.education}
                    onChange={e => setFormData({...formData, education: e.target.value})}
                    placeholder="F.eks. Master i rettsvitenskap"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kort beskrivelse av relevant erfaring *</label>
                  <textarea 
                    required
                    value={formData.experience_summary}
                    onChange={e => setFormData({...formData, experience_summary: e.target.value})}
                    rows="3"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sertifiseringer / kurs</label>
                  <input 
                    type="text" 
                    value={formData.certifications}
                    onChange={e => setFormData({...formData, certifications: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" 
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">7. Profiltekst</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kort presentasjon *</label>
                <textarea 
                  required maxLength="600"
                  value={formData.bio}
                  onChange={e => setFormData({...formData, bio: e.target.value})}
                  rows="4"
                  placeholder="Maks 600 tegn. Denne teksten kan brukes i din utrederprofil dersom søknaden godkjennes."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                ></textarea>
                <div className="text-right text-[10px] text-gray-400 mt-1">{formData.bio.length} / 600</div>
              </div>
            </div>

            {/* HMS Varsling Utrederkvalifisering */}
            <div className="border-t border-gray-100 pt-6">
              <div className="bg-indigo-50/50 rounded-2xl p-6 border border-indigo-100">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold text-gray-900">HMS Varsling Utrederkvalifisering</h3>
                      <span className="bg-indigo-100 text-indigo-700 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider">VALGFRITT</span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Ønsker du å gjennomføre HMS Varsling sitt kvalifikasjonskurs for uavhengige utredere? Kurset gir en strukturert gjennomgang av metodikk, habilitet, taushetsplikt, saksbehandling og profesjonell håndtering av varslingssaker.
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-indigo-600 font-bold text-lg">5 000 kr</div>
                    <div className="text-[10px] text-gray-400">eks. mva.</div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, qualification_course_interest: !prev.qualification_course_interest }))}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
                    formData.qualification_course_interest
                      ? 'bg-indigo-600 text-white shadow-lg'
                      : 'bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50'
                  }`}
                >
                  {formData.qualification_course_interest ? (
                    <SafeIcon icon={FiCheckCircle} className="text-xl" />
                  ) : (
                    <SafeIcon icon={FiBookOpen} className="text-xl" />
                  )}
                  {formData.qualification_course_interest 
                    ? 'Interesse registrert' 
                    : 'Jeg ønsker informasjon om kvalifikasjonskurset'
                  }
                </button>

                <p className="mt-4 text-[11px] text-gray-500 italic">
                  Dette er ikke et krav for å sende søknaden. Hvis du velger dette, registrerer vi interessen din og følger opp med informasjon om kurs og godkjenning.
                </p>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">8. Kapasitet</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Antall aktive saker samtidig</label>
                  <select 
                    value={formData.max_capacity}
                    onChange={e => setFormData({...formData, max_capacity: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5+">5+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tilgjengelighet</label>
                  <select 
                    value={formData.availability}
                    onChange={e => setFormData({...formData, availability: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="now">Tilgjengelig nå</option>
                    <option value="1_week">Innen 1 uke</option>
                    <option value="2_weeks">Innen 2 uker</option>
                    <option value="agreement">Etter avtale</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100 space-y-4">
              <h3 className="text-lg font-bold text-gray-900 mb-4">9. Bekreftelser</h3>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" required checked={formData.confirm_correct} onChange={e => setFormData({...formData, confirm_correct: e.target.checked})} className="w-5 h-5 text-indigo-600 rounded" />
                  <span className="text-sm text-gray-700">Jeg bekrefter at opplysningene jeg har gitt er korrekte.</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" required checked={formData.confirm_confidentiality} onChange={e => setFormData({...formData, confirm_confidentiality: e.target.checked})} className="w-5 h-5 text-indigo-600 rounded" />
                  <span className="text-sm text-gray-700">Jeg forplikter meg til å ivareta taushetsplikt og konfidensialitet.</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" required checked={formData.confirm_conflict} onChange={e => setFormData({...formData, confirm_conflict: e.target.checked})} className="w-5 h-5 text-indigo-600 rounded" />
                  <span className="text-sm text-gray-700">Jeg forstår at habilitet og interessekonflikter skal vurderes før jeg påtar meg en sak.</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={formData.terms_acceptance}
                    onChange={e => setFormData({...formData, terms_acceptance: e.target.checked})}
                    className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500" 
                  />
                  <span className="text-sm text-gray-700 font-medium">
                    Jeg bekrefter mine kvalifikasjoner og godtar <button type="button" onClick={() => setShowTerms(true)} className="text-indigo-600 font-bold underline">taushets- og profesjonsvilkårene</button>.
                  </span>
                </label>
              </div>
            </div>

            <button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full bg-indigo-600 text-white py-4 rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-lg flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {isSubmitting ? 'Sender...' : 'Send søknad som utreder'}
              <SafeIcon icon={FiArrowRight} />
            </button>
          </form>
        </div>
      </div>

      {/* TERMS MODAL */}
      <AnimatePresence>
        {showTerms && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowTerms(false)}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white w-full max-w-2xl max-h-[80vh] rounded-2xl overflow-hidden flex flex-col shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-indigo-50 text-indigo-900">
                <h2 className="text-xl font-bold flex items-center gap-2"><SafeIcon icon={FiShield} /> Utreder-vilkår & Taushetsplikt</h2>
                <button onClick={() => setShowTerms(false)} className="p-2 hover:bg-indigo-200 rounded-full text-indigo-900"><SafeIcon icon={FiX} /></button>
              </div>
              <div className="p-8 overflow-y-auto prose prose-sm max-w-none text-gray-700 space-y-6">
                <section>
                  <h3 className="font-bold text-gray-900">1. Taushetsplikt</h3>
                  <p>Utrederen påtar seg en streng og livsvarig taushetsplikt om alle opplysninger vedkommende får kjennskap til gjennom varslingssaker i portalen. Dette inkluderer varslerens identitet, innhold i varsler, og bedriftens interne forhold.</p>
                </section>
                <section>
                  <h3 className="font-bold text-gray-900">2. Habilitet og Uavhengighet</h3>
                  <p>Utrederen plikter å vurdere sin egen habilitet før hver sak påbegynnes. Ved interessekonflikt skal utrederen straks melde fra og trekke seg fra saken.</p>
                </section>
                <section>
                  <h3 className="font-bold text-gray-900">3. Profesjonell Standard</h3>
                  <p>Alle utredninger skal utføres i tråd med Arbeidsmiljølovens krav til forsvarlig saksbehandling og god skikk for faktaundersøkelser. Kontradiksjonsprinsippet skal alltid ivaretas.</p>
                </section>
              </div>
              <div className="p-6 border-t border-gray-100 bg-gray-50">
                <button 
                  onClick={() => {
                    setFormData({...formData, terms_acceptance: true});
                    setShowTerms(false);
                  }}
                  className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold hover:bg-indigo-700 transition-colors"
                >
                  Jeg bekrefter mine forpliktelser som utreder
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UtrederRegister;